/** @jsx jsx */
// CDS
import { css, jsx } from '@emotion/react';

import { LONG_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Grid, Link, Typography, Typography2, breakpoints } from '@coursera/cds-core';

import type { Asset, Maybe, Scalars } from 'bundles/tutorials/__generated__/baseContentfulTypes';
import { POST_CARD_DIMENSIONS } from 'bundles/tutorials/constants';

import _t from 'i18n!nls/tutorials';

type Props = {
  title: string;
  cardImage?: Maybe<Pick<Asset, 'title' | 'description' | 'width' | 'height' | 'url'>>;
  href: string;
  description: string;
  publishedDate?: Scalars['DateTime'];
  dataE2EAttribute?: string;
};

const styles = {
  container: () => css`
    display: block;
    padding: 0 var(--cds-spacing-150);
    margin: 0 var(--cds-spacing-800) var(--cds-spacing-800);
    margin-bottom: var(--cds-spacing-800);
    overflow: hidden;
    ${breakpoints.down('xs')} {
      margin: 0 0 var(--cds-spacing-800);
    }
  `,
  body: () => css`
    padding-right: var(--cds-spacing-400);
    ${breakpoints.down('xs')} {
      padding-right: 0;
    }
  `,
  header: () => css`
    margin-bottom: var(--cds-spacing-300);
  `,
  imageWrapper: () => css`
    border-radius: 4px;
    box-shadow: 1px 3px 8px 0 var(--cds-color-darken-300);
    height: 0;
    overflow: hidden;
    padding-bottom: ${(POST_CARD_DIMENSIONS.height / POST_CARD_DIMENSIONS.width) * 100}%;
    position: relative;
    width: 100%;
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
  image: () => css`
    display: block;
    width: 100%;
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
  publishedDate: () => css`
    margin-top: var(--cds-spacing-300);
  `,
};

const PostCard = ({ title, description, cardImage, href, publishedDate, dataE2EAttribute }: Props) => (
  <div css={styles.container} data-e2e={dataE2EAttribute}>
    <Link variant="quiet" href={href}>
      <Grid container>
        <Grid item xs={12} sm={cardImage ? 9 : 12} css={styles.body}>
          <Typography variant="h1semibold" component="h2" css={styles.header}>
            {title}
          </Typography>
          <Typography variant="h2" component="p">
            {description}
          </Typography>
          <Typography2 variant="bodyPrimary" component="div" color="supportText" css={styles.publishedDate}>
            {formatDateTimeDisplay(publishedDate, LONG_DATE_ONLY_DISPLAY)}
          </Typography2>
        </Grid>
        {cardImage && cardImage.url !== null && (
          <Grid item sm={3}>
            <div css={styles.imageWrapper}>
              <img css={styles.image} src={cardImage.url} alt={cardImage.description || _t('Preview image')} />
            </div>
          </Grid>
        )}
      </Grid>
    </Link>
  </div>
);

export default PostCard;
